import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, AlertCircle } from 'lucide-react';
import { Section, SectionTitle } from '@/components/shared/Section';
import { fadeUp, stagger } from '@/lib/motion';
import { usePrice } from '@/lib/priceMode';
import { getHomeOffers } from '@/lib/api';

// Normaliza specs que puede venir como string, array o objeto
const normalizeSpecs = (s) => {
  if (!s) return '';
  if (typeof s === 'string') return s;
  if (Array.isArray(s)) return s.filter(Boolean).join(' · ');
  if (typeof s === 'object') return Object.entries(s).map(([k, v]) => `${k}: ${v}`).join(' · ');
  return String(s);
};

const fromProduct = (p) => {
  // images puede ser: array de strings, array de objetos {url,alt,...}, o array vacío
  const firstImg = Array.isArray(p.images) && p.images.length > 0
    ? (typeof p.images[0] === 'string' ? p.images[0] : p.images[0]?.url)
    : null;
  // priceOffer: prefer the human-formatted price_display; fallback to numeric price stringified
  const offerPrice = p.price_display || p.price || '';
  return {
    slug: p.slug,
    name: p.name,
    brand: p.brand,
    category: p.category || '',
    description: p.description || p.short_description || '',
    specs: normalizeSpecs(p.specs),
    image: firstImg || p.image || null,
    priceOriginal: p.price_original || p.priceOriginal || null,
    priceOffer: offerPrice,
    discount: p.discount || '',
    link: p.slug ? `/productos/${p.slug}` : '/contacto',
    isLiquidation: !!p.liquidacion,
  };
};

const OffersBlock = () => {
  const [offers, setOffers] = useState(null); // null = loading
  const [error, setError] = useState(false);
  const { format: formatPrice } = usePrice();

  useEffect(() => {
    // ÚNICA query: trae productos donde liquidacion=true OR show_in_offers=true.
    // Sin fallback hardcoded (los precios placeholder $9.999.999 dañaban la marca).
    (async () => {
      try {
        const r = await getHomeOffers(24);
        const list = Array.isArray(r.data) ? r.data : [];
        setOffers(list.map(fromProduct));
      } catch {
        setError(true);
        setOffers([]);
      }
    })();
  }, []);

  const loading = offers === null;
  const isEmpty = !loading && offers.length === 0;

  return (
    <Section data-testid="offers-block">
      <SectionTitle
        badge="OFERTAS ESPECIALES"
        title="Equipos en oferta"
        subtitle="Stock limitado de equipos certificados con garantía ALLCOM. Última generación a precios de liquidación."
        center
      />

      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto" data-testid="offers-loading">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="rounded-2xl bg-slate-100 animate-pulse h-96" />
          ))}
        </div>
      ) : isEmpty ? (
        <div className="max-w-md mx-auto text-center py-12" data-testid="offers-empty">
          <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-slate-100 flex items-center justify-center">
            <AlertCircle className="w-6 h-6 text-slate-400" strokeWidth={1.5} />
          </div>
          <h3 className="text-lg font-semibold text-[#0A2540] mb-2" style={{ fontFamily: 'Syne,sans-serif' }}>
            {error ? 'No pudimos cargar las ofertas' : 'No hay ofertas activas en este momento'}
          </h3>
          <p className="text-sm text-[#64748B] leading-relaxed mb-6">
            {error ? 'Reintenta en unos segundos o contáctanos para conocer disponibilidad y precios actualizados.' : 'Estamos preparando nuevas oportunidades. Mientras tanto, contáctanos para una cotización personalizada.'}
          </p>
          <Link to="/contacto" className="inline-flex items-center gap-1.5 px-5 py-2.5 text-xs font-semibold text-white bg-[#0EA5E9] rounded-xl hover:bg-sky-600 shadow-lg transition-all" data-testid="offers-empty-cta">
            Solicitar cotización <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      ) : (
        <motion.div
          variants={stagger}
          initial="hidden"
          animate="visible"
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto"
          data-testid="offers-grid-dynamic"
        >
          {offers.map((product, idx) => (
            <motion.div
              key={product.slug || `${product.name}-${idx}`}
              variants={fadeUp}
              className="group relative bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-xl hover:border-red-500/30 transition-all duration-300"
              data-testid={`offer-card-${product.slug || idx}`}
            >
              {/* Badges */}
              <div className="absolute top-4 left-4 right-4 z-10 flex items-center justify-between">
                <span className={`px-3 py-1 text-[10px] font-bold uppercase tracking-wider rounded-full shadow-lg text-white ${product.isLiquidation ? 'bg-red-500' : 'bg-[#0EA5E9]'}`}>
                  {product.isLiquidation ? 'Liquidación' : 'Destacado'}
                </span>
                {product.discount && (
                  <span className="px-3 py-1 text-xs font-bold bg-[#FE7A15] text-white rounded-full shadow-lg">
                    {product.discount}
                  </span>
                )}
              </div>

              {/* Image */}
              <div className="aspect-square bg-gradient-to-br from-slate-50 to-slate-100 overflow-hidden flex items-center justify-center p-12">
                {product.image ? (
                  <img src={product.image} alt={`${product.brand} ${product.name}`} className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                ) : (
                  <div className="text-center">
                    <div className="text-5xl font-bold mb-2" style={{ fontFamily: 'Syne,sans-serif', color: '#0191B4' }}>{product.brand}</div>
                    <div className="text-xs text-slate-400 uppercase tracking-wider">{product.category}</div>
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="p-5 text-center">
                <div className="flex items-center gap-2 mb-2 justify-center">
                  {product.brand && <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{product.brand}</span>}
                  {product.category && <span className="text-[10px] px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full font-medium">{product.category}</span>}
                </div>
                <h3 className="text-base font-semibold text-[#0A2540] mb-2 group-hover:text-red-500 transition-colors" style={{ fontFamily: 'Syne,sans-serif' }}>{product.name}</h3>
                {product.description && <p className="text-xs text-[#64748B] leading-relaxed mb-3 line-clamp-2">{product.description}</p>}
                {product.specs && <div className="text-[10px] text-slate-400 mb-4 leading-relaxed line-clamp-2">{product.specs}</div>}
                <div className="mb-4">
                  {product.priceOriginal && product.priceOriginal !== product.priceOffer && product.priceOriginal !== '$9.999.999' && (
                    <div className="text-xs text-slate-400 line-through tabular-nums">{formatPrice(product.priceOriginal)}</div>
                  )}
                  {product.priceOffer && product.priceOffer !== '$9.999.999' ? (
                    <>
                      <div className="text-2xl font-bold text-red-500 tabular-nums" style={{ fontFamily: 'Syne,sans-serif' }}>{formatPrice(product.priceOffer)}</div>
                      <div className="text-[10px] text-slate-500 mt-1">+ IVA · Stock limitado</div>
                    </>
                  ) : (
                    <>
                      <div className="text-2xl font-bold text-[#0EA5E9]" style={{ fontFamily: 'Syne,sans-serif' }}>Cotizar</div>
                      <div className="text-[10px] text-slate-500 mt-1">Precio sujeto a stock</div>
                    </>
                  )}
                </div>
                <Link to={product.link} className="inline-flex items-center gap-1.5 px-5 py-2.5 text-xs font-semibold text-white bg-red-500 rounded-xl hover:bg-red-600 shadow-lg transition-all">
                  Cotizar oferta <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </motion.div>
          ))}
        </motion.div>
      )}

      {!loading && !isEmpty && (
        <div className="mt-8 max-w-2xl mx-auto text-center">
          <p className="text-xs text-slate-500">
            Todos los equipos en oferta incluyen <span className="font-semibold text-[#0A2540]">garantía de 3 años</span>, soporte técnico 24/7 y disponibilidad inmediata. Stock limitado hasta agotar existencias.
          </p>
        </div>
      )}
    </Section>
  );
};

export default OffersBlock;
